/**
 * useDocumentClassificationCache
 *
 * MINSKY-17131: fresh citations store `docClassificationLevel` directly (see
 * `referencesYMap.addReference`), but references added before this shipped are
 * missing it. This hook backfills those by looking the owning document up once per
 * unique `docId` via `GET /api/documents/{docId}`, and caches the result at module
 * scope so every consumer (margin rail badges, the whole-document AI Assistant view)
 * shares one in-flight/settled lookup per document instead of re-fetching.
 *
 * The endpoint used (`getDocumentContent`) returns the full document payload (segments,
 * entities, etc.) — heavier than ideal for "just the classification level" — but it's the
 * only existing endpoint that exposes `classificationLevel`, and this path is only hit for
 * legacy references, once per docId, ever (module-level cache never expires within a session).
 */

import {useCallback, useEffect, useRef, useState} from 'react';
import {getDocumentContent} from '../utils/api/document';

/** @type {Map<string, string|null>} docId -> classificationLevel (resolved) */
const resolvedCache = new Map();
/** @type {Map<string, Promise<string|null>>} docId -> in-flight lookup */
const pendingCache = new Map();

function fetchClassificationLevel(docId) {
    if (resolvedCache.has(docId)) {
        return Promise.resolve(resolvedCache.get(docId));
    }
    if (pendingCache.has(docId)) {
        return pendingCache.get(docId);
    }

    const promise = getDocumentContent(docId)
        .then((doc) => {
            const level = doc?.classificationLevel ?? null;
            resolvedCache.set(docId, level);
            pendingCache.delete(docId);
            return level;
        })
        .catch((error) => {
            console.error(`[useDocumentClassificationCache] Failed to fetch document ${docId}:`, error);
            pendingCache.delete(docId);
            return null;
        });

    pendingCache.set(docId, promise);
    return promise;
}

/**
 * Resolve the classification level for a single docId, only fetching when needed.
 *
 * @param {string|number|null} docId - Pass null/undefined to skip the lookup (e.g. when the
 *   reference already carries its own `docClassificationLevel`).
 * @returns {{level: string|null, loading: boolean}}
 */
export function useDocumentClassificationCache(docId) {
    const key = docId != null ? String(docId) : null;
    const [level, setLevel] = useState(() => (key ? resolvedCache.get(key) ?? null : null));
    const [loading, setLoading] = useState(() => Boolean(key) && !resolvedCache.has(key));

    useEffect(() => {
        if (!key) {
            setLevel(null);
            setLoading(false);
            return;
        }
        if (resolvedCache.has(key)) {
            setLevel(resolvedCache.get(key));
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        fetchClassificationLevel(key).then((resolved) => {
            if (!cancelled) {
                setLevel(resolved);
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [key]);

    return {level, loading};
}

/**
 * Imperative variant for call sites that need to resolve many docIds at once outside of a
 * single component's render (e.g. the whole-document view building its above-(U) list).
 *
 * @param {(string|number)[]} docIds
 * @returns {Promise<Map<string, string|null>>} docId (as string) -> classificationLevel
 */
export async function resolveClassificationLevels(docIds) {
    const uniqueIds = Array.from(new Set((docIds || []).filter((id) => id != null).map(String)));
    const results = await Promise.all(uniqueIds.map((id) => fetchClassificationLevel(id)));
    const map = new Map();
    uniqueIds.forEach((id, i) => map.set(id, results[i]));
    return map;
}

/**
 * Test-only: clear the module-level cache between test cases.
 */
export function __resetDocumentClassificationCacheForTests() {
    resolvedCache.clear();
    pendingCache.clear();
}

export default useDocumentClassificationCache;
